import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Building2, Phone, Briefcase, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import toast from 'react-hot-toast'
const SithabileLogo = '/sithabile-fingerprint-logo.png'

const completeRegistrationSchema = z.object({
  full_name: z.string().min(1, 'Full name is required').max(100),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number').optional().or(z.literal('')),
  department: z.string().optional(),
  job_title: z.string().optional(),
})

type CompleteRegistrationForm = z.infer<typeof completeRegistrationSchema>

export function CompleteRegistrationPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [oauthData, setOauthData] = useState<any>(null)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CompleteRegistrationForm>({
    resolver: zodResolver(completeRegistrationSchema),
  })

  useEffect(() => {
    // Check if user is logged in
    if (user) {
      // For now, just pre-fill with existing user data
      setValue('full_name', user.full_name || `${user.first_name} ${user.last_name}`)
      setValue('department', user.department || '')
      setValue('job_title', user.job_title || '')
      
      // Mock OAuth data for display
      setOauthData({
        email: user.email,
        preferred_username: user.email
      })
    }
  }, [user, setValue])

  const onSubmit = async (data: CompleteRegistrationForm) => {
    setIsSubmitting(true)
    
    try {
      const response = await fetch('/api/v1/auth/oauth/complete-registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error('Failed to complete registration')
      }

      toast.success('Registration completed successfully!')
      
      // Redirect to dashboard
      navigate('/dashboard')
    } catch (error) {
      console.error('Registration error:', error)
      toast.error('Failed to complete registration')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#000742] dark:to-[#0a0f3f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={SithabileLogo} alt="Sithabile" className="h-12" />
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-[#5869ff]/20">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Complete Your Registration
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Welcome! Please complete your profile to get started.
            </p>
          </div>

          {/* OAuth Info */}
          {oauthData && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-[#5869ff]/10 rounded-lg border border-blue-200 dark:border-[#5869ff]/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-[#5869ff] mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 dark:text-white mb-1">
                    Microsoft Account Connected
                  </p>
                  <p className="text-blue-700 dark:text-gray-300">
                    {oauthData.email || oauthData.preferred_username}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  {...register('full_name')}
                  type="text"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-500">{errors.full_name.message}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  {...register('phone')}
                  type="tel"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                  placeholder="+1234567890"
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Department
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  {...register('department')}
                  type="text"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                  placeholder="IT Department"
                />
              </div>
            </div>

            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Job Title
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  {...register('job_title')}
                  type="text"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:border-transparent"
                  placeholder="Storage Administrator"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
            >
              {isSubmitting ? 'Completing Registration...' : 'Complete Registration'}
            </button>
          </form>

          {/* Note */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-[#1a1f5c]/30 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Note:</strong> Your account has been created with basic user permissions. 
              You will have access to view the dashboard only. Contact your administrator for 
              additional permissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}